import React, {useState, useRef, useEffect} from "react";
import {MdPlayCircleOutline, MdPauseCircleOutline} from "react-icons/md";
import {LuVolume2} from "react-icons/lu";
import {IoMdPlay} from "react-icons/io";
import Mixpanel from "../utils/mixpanel";
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";

function getSeconds(time: string): number {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    return hours * 3600 + minutes * 60 + seconds;
}

export default function AudioPlayer({audioFile}: {audioFile: File | null}) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [durationPlayer, setDurationPlayer] = useState(0);
    const [volume, setVolume] = useState(1);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isVolumeControlVisible, setIsVolumeControlVisible] = useState(false);
    useEffect(() => {
        if (audioFile) {
            const url = URL.createObjectURL(audioFile);
            setAudioUrl(url);

            // Reset states
            setCurrentTime(0);
            setIsPlaying(false);
            setDurationPlayer(0);

            if (audioRef.current) {
                audioRef.current.src = url;
                audioRef.current.load();

                const handleLoadedMetadata = () => {
                    const duration = audioRef.current?.duration;
                    // Use the stored duration if available, otherwise use the audio element's duration
                    const finalDuration =
                        (audioFile as any).duration || duration || 0;
                    setDurationPlayer(finalDuration);
                };

                audioRef.current.addEventListener(
                    "loadedmetadata",
                    handleLoadedMetadata
                );

                return () => {
                    audioRef.current?.removeEventListener(
                        "loadedmetadata",
                        handleLoadedMetadata
                    );
                    URL.revokeObjectURL(url);
                };
            }
        }
    }, [audioFile]);

    // Separate useEffect for audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        // Add event listeners
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);

        // Cleanup
        return () => {
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
        };
    }, [audioRef.current]);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            Mixpanel.track("Pause");
        } else {
            // Ensure we start from beginning if ended
            if (audio.ended) {
                audio.currentTime = 0;
            }
            audio
                .play()
                .catch((error) => console.error("Error playing audio:", error));
            Mixpanel.track("Play");
        }
        setIsPlaying(!isPlaying);
    };

    const handleSkip = (seconds: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime += seconds;
    };

    const handleSliderChange = (value: number[]) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = value[0];
        setCurrentTime(value[0]);
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleVolumeChange = (value: number[]) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newVolume = value[0];
        audio.volume = newVolume;
        setVolume(newVolume);
    };

    const handlePlaybackRateChange = (newRate: number) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.playbackRate = newRate;
        setPlaybackRate(newRate);
    };

    const handleDownload = () => {
        if (audioFile) {
            const url = URL.createObjectURL(audioFile);
            const a = document.createElement("a");
            a.href = url;
            a.download = audioFile.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            Mixpanel.track("Download");
        }
    };

    return (
        <div className="w-full flex flex-col items-center justify-center gap-2">
            <audio
                ref={audioRef}
                src={audioUrl || undefined}
                preload="metadata"
            />
            {/* Controls Row */}
            <div className="flex flex-row items-center justify-center gap-8 w-full mb-2">
                {/* Playback speed with dropdown */}
                <div className="flex flex-row items-center gap-2">
                    <Menu
                        as="div"
                        className="relative inline-block text-left"
                        style={{minWidth: "50px"}}
                    >
                        <MenuButton className="px-2 py-1 rounded bg-white text-[16px] font-medium text-black leading-[24px] tracking-[0.1px] select-none hover:text-primary w-full">
                            {playbackRate.toFixed(1)}X
                        </MenuButton>
                        <MenuItems className="absolute left-0 mt-2 w-20 origin-top-left bg-white border border-gray-200 rounded shadow-lg z-10">
                            {[0.5, 1, 1.5, 2].map((rate) => (
                                <MenuItem key={rate}>
                                    {({active}) => (
                                        <button
                                            className={`w-full px-2 py-1 text-left text-sm ${active ? "bg-indigo-100" : ""}`}
                                            onClick={() =>
                                                handlePlaybackRateChange(rate)
                                            }
                                        >
                                            {rate.toFixed(1)}x
                                        </button>
                                    )}
                                </MenuItem>
                            ))}
                        </MenuItems>
                    </Menu>
                </div>
                {/* Play/Pause Button */}
                <button
                    onClick={togglePlayPause}
                    className="group flex items-center justify-center rounded-full w-12 h-12 bg-white hover:scale-105 transition-transform hover:cursor-pointer hover:border-primary"
                    aria-label={isPlaying ? "Pause" : "Play"}
                >
                    {isPlaying ? (
                        <MdPauseCircleOutline className="text-black group-hover:text-primary w-12 h-12" />
                    ) : (
                        <MdPlayCircleOutline className="text-black group-hover:text-primary w-12 h-12" />
                    )}
                </button>
                {/* Volume Icon and slider */}
                <div className="flex flex-row items-center gap-2">
                    <div className="relative flex items-center min-w-[40px] justify-center">
                        <button
                            onClick={() => {
                                setVolume(volume === 0 ? 1 : 0);
                                setIsVolumeControlVisible(
                                    !isVolumeControlVisible
                                );
                            }}
                            className="flex items-center justify-center"
                            aria-label="Toggle mute"
                        >
                            <LuVolume2
                                className="text-[20px] text-black hover:text-indigo-500"
                                size={24}
                            />
                        </button>
                        {isVolumeControlVisible && (
                            <div className="absolute left-8 top-1/2 transform -translate-y-1/2 z-50">
                                <input
                                    type="range"
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    value={volume}
                                    onChange={(e) =>
                                        handleVolumeChange([
                                            parseFloat(e.target.value),
                                        ])
                                    }
                                    className="w-24 h-2 accent-indigo-500"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Progress Bar Row */}
            <div className="flex flex-row items-center w-full gap-2">
                <span
                    className="text-[14px] text-secondary"
                    style={{minWidth: 32}}
                >
                    {formatTime(currentTime)}
                </span>
                <div className="flex-grow">
                    <div
                        className="w-full h-2 bg-gray-200 rounded-full cursor-pointer relative"
                        onClick={(e) => {
                            const rect =
                                e.currentTarget.getBoundingClientRect();
                            const x = e.clientX - rect.left;
                            const percentage = x / rect.width;
                            handleSliderChange([percentage * durationPlayer]);
                        }}
                    >
                        <div
                            className="h-2 bg-purple-500 rounded-full absolute top-0 left-0"
                            style={{
                                width: `${(currentTime / durationPlayer) * 100}%`,
                            }}
                        />
                    </div>
                </div>
                <span
                    className="text-[14px] text-secondary"
                    style={{minWidth: 32}}
                >
                    {formatTime(durationPlayer)}
                </span>
            </div>
        </div>
    );
}
